import { Button, type ButtonProps } from '../primitives/button.js'
import { ExpandLess, ExpandMore } from '@tunasong/icons'
import cn from 'classnames'
import type { FC } from 'react'
import { useState } from 'react'
import { useDimensions } from '../layout/dimensions.js'
import { ListItemIcon, Menu, MenuContent, MenuItem, MenuTrigger } from '../primitives/index.js'
import moduleStyles from './menu-button.module.css'

interface MenuButtonItem {
  Icon: FC
  name: string
}

export interface MenuButtonSelector<T extends MenuButtonItem> extends Omit<ButtonProps, 'onChange'> {
  items: T[]
  selected?: T
  onChange(item: T): void
}

export const MenuButtonSelector = <T extends MenuButtonItem>(props: MenuButtonSelector<T>) => {
  const { items, selected, onChange, ...restProps } = props

  const { isSmallOrSmaller } = useDimensions()

  // Only drives the expand/collapse icon; the menu itself is uncontrolled.
  const [open, setOpen] = useState(false)

  return (
    <Menu onOpenChange={setOpen}>
      <MenuTrigger
        render={
          <Button
            {...restProps}
            className={cn(moduleStyles.button, restProps.className)}
            disabled={items.length <= 1}
            startIcon={selected?.Icon && <selected.Icon />}
            endIcon={open ? <ExpandLess /> : <ExpandMore />}
            aria-label="Layout Selector Button"
          >
            {isSmallOrSmaller ? '' : selected?.name}
          </Button>
        }
      />
      <MenuContent>
        {items.map(item => (
          <MenuItem key={item.name} disabled={item?.name === selected?.name} onClick={() => onChange(item)}>
            <ListItemIcon>{<item.Icon />}</ListItemIcon>
            {item.name}
          </MenuItem>
        ))}
      </MenuContent>
    </Menu>
  )
}

export default MenuButtonSelector
